import { faArrowDownLong, faListTimeline, faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, DateTimePicker, NotificationDispatch, Tab, TabList, TabPanel, Tabs, TextField } from '@tryhackme/thm-ui-components';
import { useMemo, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'src/common/form';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { useGetUserQuery } from 'src/common/slices';
import { useLazyGetActivityQuery } from '../categorization.slice';
import { StyledTabContent } from '../categorization.styles';
import type { GetActivityRequest, GetActivityResponse } from '../categorization.types';
import { LabeledActivity } from '../components/labeled-activity/labeled-activity';
import { StyledForm, StyledFormGroup, StyledRow } from '../components/rule/rule.styles';
import { UnlabeledActivity } from '../components/unlabeled-activity/unlabeled-activity';
import { activityInitialValue, activityValidation } from './activity.constants';
import { StyledBody, StyledHeader, StyledIcon } from './activity.styles';
import type { ActivityProps } from './activity.types';
export const Activity = ({
  rooms
}: ActivityProps) => {
  const {
    data: user
  } = useGetUserQuery();
  const loggedInUserId = useMemo(() => user?.data?.user?._id, [user]);
  const [getActivityQuery, {
    isLoading
  }] = useLazyGetActivityQuery();
  const [activities, setActivities] = useState<GetActivityResponse[]>([]);
  const methods = useForm({
    resolver: yupResolver(activityValidation),
    defaultValues: activityInitialValue(loggedInUserId || ''),
    mode: 'all'
  });
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: {
      errors
    }
  } = methods;
  const [roomId, startDate] = useWatch({
    control,
    name: ['roomId', 'startDate']
  });
  const selectedRoom = useMemo(() => rooms.find(option => option.value === roomId), [roomId, rooms]);
  const onSubmit = async (data: GetActivityRequest) => {
    try {
      const response = await getActivityQuery(data).unwrap();
      setActivities(response);
    } catch (error) {
      if (isQueryErrorWithMessage(error)) {
        NotificationDispatch('error', error?.data?.message);
      } else {
        // eslint-disable-next-line no-console
        console.error(error);
        NotificationDispatch('error', 'Something went wrong');
      }
    }
    reset(data);
  };
  return <Tabs defaultValue="activity" data-sentry-element="Tabs" data-sentry-component="Activity" data-sentry-source-file="activity.tsx">
      <TabList data-sentry-element="TabList" data-sentry-source-file="activity.tsx">
        <Tab value="activity" data-sentry-element="Tab" data-sentry-source-file="activity.tsx">
          <FontAwesomeIcon icon={faListTimeline} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="activity.tsx" /> Activity
        </Tab>
      </TabList>
      <TabPanel value="activity" data-sentry-element="TabPanel" data-sentry-source-file="activity.tsx">
        <StyledTabContent data-sentry-element="StyledTabContent" data-sentry-source-file="activity.tsx">
          <StyledHeader data-sentry-element="StyledHeader" data-sentry-source-file="activity.tsx">
            <FormProvider {...methods} data-sentry-element="FormProvider" data-sentry-source-file="activity.tsx">
              <StyledForm data-sentry-element="StyledForm" data-sentry-source-file="activity.tsx">
                <StyledRow data-sentry-element="StyledRow" data-sentry-source-file="activity.tsx">
                  <StyledFormGroup data-sentry-element="StyledFormGroup" data-sentry-source-file="activity.tsx">
                    <TextField {...register('userId')} data-testid="userId" placeholder="User Id" variant="input" isRequired maxLength={100} isError={Boolean(errors.userId)} data-sentry-element="TextField" data-sentry-source-file="activity.tsx" />

                    <Controller control={control} name="roomId" render={({
                    field: {
                      onChange
                    }
                  }) => <TextField placeholder="Room" variant="select" options={rooms} selected={selectedRoom} isMulti={false} isSearchable isError={Boolean(errors.roomId)} handleSelection={option => onChange(option?.value)} />} data-sentry-element="Controller" data-sentry-source-file="activity.tsx" />
                  </StyledFormGroup>
                </StyledRow>
                <StyledRow data-sentry-element="StyledRow" data-sentry-source-file="activity.tsx">
                  <StyledFormGroup data-sentry-element="StyledFormGroup" data-sentry-source-file="activity.tsx">
                    <Controller control={control} name="startDate" render={({
                    field: {
                      onChange,
                      value
                    }
                  }) => <DateTimePicker id="startDate" selected={value ? new Date(value) : undefined} dateFormat="MMM do yyyy HH:mm" isClearable placeholderText="Start date" maxDate={new Date()} showTimeSelect timeIntervals={15} timeFormat="HH:mm" onChange={onChange} />} data-sentry-element="Controller" data-sentry-source-file="activity.tsx" />

                    <Controller control={control} name="endDate" render={({
                    field: {
                      onChange,
                      value
                    }
                  }) => <DateTimePicker id="endDate" selected={value ? new Date(value) : undefined} dateFormat="MMM do yyyy HH:mm" isClearable placeholderText="End date" minDate={startDate ? new Date(startDate) : new Date()} maxDate={new Date()} showTimeSelect timeIntervals={15} timeFormat="HH:mm" onChange={onChange} />} data-sentry-element="Controller" data-sentry-source-file="activity.tsx" />
                  </StyledFormGroup>
                </StyledRow>
              </StyledForm>
              <Button color="add" isDisabled={isLoading}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={handleSubmit(onSubmit)} data-sentry-element="Button" data-sentry-source-file="activity.tsx">
                <FontAwesomeIcon icon={faMagnifyingGlass} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="activity.tsx" />
                Search
              </Button>
            </FormProvider>
          </StyledHeader>

          <StyledBody data-sentry-element="StyledBody" data-sentry-source-file="activity.tsx">
            {activities.map((activity, index) => <>
                {index === 0 ? null : <StyledIcon icon={faArrowDownLong} />}
                {activity.categories.length > 0 ? <LabeledActivity activity={activity} /> : <UnlabeledActivity activity={activity} />}
              </>)}
          </StyledBody>
        </StyledTabContent>
      </TabPanel>
    </Tabs>;
};